
import { Component, Prop, Vue, Watch, Provide } from "vue-property-decorator"
import { Getter, State } from "vuex-class"
import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { Clause, getDefaultSearchClauseParameters, SearchClauseParameters } from "@/types/view"

import vSelect from "vue-select"
import ParameterValue from "../Controls/ParameterValue.vue"
import { DataType, DynamicParameter, Operator, operatorLabels, operatorsByFieldType, InsightDetails, InsightCalculation, InsightCalculationTotal } from "@/types/insights"
import CalculationDataType from "@/types/field"
import { CalculationType } from "@/types"
import Guid from "@/scripts/guid"
import { dataTypeParameters } from "@/types/dataType"
import { UserColumn } from "@/types/userColumns"
import { OperatorLabel } from "@/types/insights/operators"
import { CurrentUser } from "@/types/user"
import { UserRole } from "@/types/caseUser"
import { TRANSITION_FOCUS_DELAY_MS } from "@/constants/animations"
import { TenantFeatures } from "@/types/tenantFeatures"
import { getCalculationColumnAsCaseColumn, getCalculationAsCaseColumn, getCalculationTotalAsCaseColumn } from "@/scripts/insights"
import HelpIcon from "../New/Page/PageComponents/HelpIcon.vue"

@Component({
	components: {
		"v-select": vSelect,
		"help-icon": HelpIcon,
		"parameter-value": ParameterValue,
		"filters": () => import("./InsightFilters.vue")
	}
})
export default class InsightFilters extends Vue {
	@Provide()
	noPopup = true

	@Prop()
	value!: Clause[]

	@Prop()
	fields!: CaseColumn[]

	@Prop()
	recordName!: string

	@Prop({ default: true })
	allowOr!: boolean

	@Prop({ default: () => [] })
	dynamicParameters!: DynamicParameter[] | null

	@Prop({ required: true })
	userFields!: UserColumn[]

	@Prop({ type: Boolean })
	utcDates!: boolean

	@Prop({ type: Object })
	insight!: InsightDetails

	@Prop({ default: null })
	parentClauseId!: string | null

	@State me!: CurrentUser;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	// field focusing related properties
	hideValueField = false
	valueFieldEventHandlerControl: HTMLElement | null = null

	filterColumns: any[] = [
		{
			key: "valid",
			label: ""
		},
		{
			key: "field",
			label: "Field"
		},
		{
			key: "operator",
			label: "Operator"
		},
		{
			key: "value",
			label: "Value"
		},
		{
			key: "buttons",
			label: ""
		}
	]
	clauses: Clause[] = []
	private Operator = Operator

	get filterableFields () : CaseColumn[] {
		return this.fields.filter(f => f.fieldExtensions?.calculationType !== CalculationType.DaysUntilOrFromToday).sortBy("label")
	}

	get filterableFieldsAndCalculations () : CaseColumn[] {
		let filterableFieldsAndCalculations = this.filterableCalculationColumns.concat(this.filterableFields)
		if (this.getIsFeatureEnabled(TenantFeatures.InsightFilterWithCalculations) &&
			(this.insight && this.insight.groupBy !== null && this.insight.groupBy !== undefined)) {
			// Calculation filters can only be added on a grouped insight
			filterableFieldsAndCalculations = this.filterableCalculations.concat(filterableFieldsAndCalculations)
			filterableFieldsAndCalculations = filterableFieldsAndCalculations.filter(x => !this.isFilterDisabled(x.key))
		}

		return filterableFieldsAndCalculations
	}

	/**
	 * Used to decide whether the info message for combining calculation filters and normal filters in an OR block is shown
	 */
	get showDisabledFiltersWarning () : boolean {
		return this.getIsFeatureEnabled(TenantFeatures.InsightFilterWithCalculations) &&
				!!this.filterableCalculations.length && // Calculations/Calculation Totals exist
				!!this.parentClauseId && // Current clause is a subclause (part of an OR block)
				this.clauses.some(c => !!c.fieldData) // Key information for calculation/calculation total filters exists
	}

	get filtersValid () : boolean {
		return this.clauses.every(clause => this.isFilterValid(clause))
	}

	get parameterTypes () {
		return dataTypeParameters.toDictionary("dataType")
	}

	get filterableCalculationColumns () : CaseColumn[] {
		if (!this.getIsFeatureEnabled(TenantFeatures.InsightsFilterByCalculationColumns)) {
			return []
		}

		return this.insight?.calculationColumns?.map(calcColumn =>
			getCalculationColumnAsCaseColumn(calcColumn.key, calcColumn.title)
		).sortBy("label") ?? []
	}

	get filterableCalculations () : CaseColumn[] {
		let calculations: CaseColumn[] = []
		let calculationTotals: CaseColumn[] = []

		if (this.insight) {
			if (this.insight.calculations) {
				// Only ungrouped calculations can be used as insight filters
				// If groupBy is set to an empty string, that means a grouping will be applied (hasGrouping is true on the calculation), hence these calculations will be removed as well
				calculations = this.insight.calculations.filter(c => c.groupBy === null || c.groupBy === undefined).map((calc: InsightCalculation) => {
					return getCalculationAsCaseColumn(calc.key, calc.label)
				}) ?? []
			}

			if (this.insight.calculationTotals) {
				// Only ungrouped calculation totals can be used as insight filters
				calculationTotals = this.insight.calculationTotals.filter(c => c.groupBy === null || c.groupBy === undefined || c.groupBy === "").map((calc: InsightCalculationTotal) => {
					return getCalculationTotalAsCaseColumn(calc.key, calc.title)
				}) ?? []
			}
			return calculations.concat(calculationTotals).sortBy("label")
		} else { return [] }
	}

	get calculationFiltersExist () : boolean {
		return this.clauses.some(x => this.isCalculationFilter(x.fieldData?.key))
	}

	/**
	 * Returns number of clauses on the insight
	 */
	get clauseCount (): number {
		let count = 0
		this.clauses.forEach(clause => {
			if (!!clause.fieldData || !!clause.fieldDataType || !!clause.value) {
				count++
			}
		})

		return count
	}

	/**
	 * Returns true if the filter is a calculation/calculation total filter, returns false if the filter is a normal or a calculation column filter
	 * @param key: The filter key
	 */
	isCalculationFilter (key: string | null | undefined) {
		if (!!this.insight && key !== null && key !== undefined) {
			return !!this.insight.calculations.find(c => "calculation." + c.key === key) ||
				!!this.insight.calculationTotals?.find(c => "calculationTotal." + c.key === key)
		}

		return false
	}

	/**
	 * Returns true when a filter is disabled to ensure that normal filters do not get mixed with calculation filters in an OR block
	 * @param key: the filter key
	 */
	isFilterDisabled (key: string | null | undefined): boolean {
		if (key && this.clauseCount > 0 && !!this.parentClauseId) {
			// If clauses exist and the filter is in an OR block
			return this.calculationFiltersExist ? !this.isCalculationFilter(key) : this.isCalculationFilter(key)
		} else {
			return false
		}
	}

	@Watch("value")
	onValueChanged () {
		if (this.clauses !== this.value) {
			Vue.set(this, "clauses", this.value)
		}
	}

	@Watch("filtersValid")
	onFiltersValueChanged (nowValid: boolean) {
		this.$emit("validChanged", this.filtersValid)
	}

	@Watch("insight.calculationTotals", { deep: true })
	calcTotalWatcher () {
		let groupedCalculationTotals = this.insight.calculationTotals?.filter(c => c.groupBy !== null && c.groupBy !== "") || []
		let calculationTotalFilters = this.clauses.filter(c => !c.orClause && this.isCalculationFilter(c.field)).filter(c => !!c.field && c.field.startsWith("calculationTotal."))

		// Remove filters using calculation totals that are no longer grouped
		groupedCalculationTotals.forEach(groupedCalc => {
			let existingCalculationFilters = calculationTotalFilters.filter(calcFilter => calcFilter.field === "calculationTotal." + groupedCalc.key).map(c => c.id)
			if (existingCalculationFilters.length > 0) {
				this.clauses.removeWhere(c => existingCalculationFilters.includes(c.id))
			}
		})

		// Remove filters for deleted calculation Totals
		let calculationTotals = this.insight.calculationTotals?.map(calcTotal => "calculationTotal." + calcTotal.key) || []
		this.clauses.removeWhere(c => !!c.field && c.field.startsWith("calculationTotal.") && !calculationTotals.includes(c.field))
		this.clauses.forEach(clause => {
			if (clause.orClause && clause.subClauses && clause.subClauses.length > 0) {
				clause.subClauses.removeWhere(s => !!s.field && s.field.startsWith("calculationTotal.") && !calculationTotals.includes(s.field))
			}
		})

		// Update calculation total filter labels if changed
		this.clauses.forEach(clause => {
			if (!clause.orClause && clause.field && clause.field.startsWith("calculationTotal.") && this.insight.calculationTotals) {
				let calculationTotalIndex = this.insight.calculationTotals.findIndex(c => "calculationTotal." + c.key === clause.field)
				if (calculationTotalIndex !== -1) {
					if (clause.label !== this.insight.calculationTotals[calculationTotalIndex].title) {
						clause.label = this.insight.calculationTotals[calculationTotalIndex].title
						if (clause.fieldData) {
							clause.fieldData.label = clause.label
						}
					}
				}
			}
		})

		// Remove or clauses that do not have sub clauses
		this.insight.clauses.removeWhere(c => !!c.orClause && !!c.subClauses && c.subClauses.length === 0)
	}

	@Watch("insight.calculations", { deep: true })
	calcWatcher () {
		let groupedCalculations = this.insight.calculations.filter(c => c.groupBy !== null)
		let calculationFilters = this.clauses.filter(c => !c.orClause && this.isCalculationFilter(c.field)).filter(c => !!c.field && c.field.startsWith("calculation."))

		// Remove filters using calculations that are no longer grouped
		groupedCalculations.forEach(groupedCalc => {
			let existingCalculationFilterIds = calculationFilters.filter(calcFilter => calcFilter.field === "calculation." + groupedCalc.key).map(c => c.id)
			if (existingCalculationFilterIds.length > 0) {
				this.clauses.removeWhere(c => existingCalculationFilterIds.includes(c.id))
			}
		})

		// Remove filters for deleted calculations
		let calculations = this.insight.calculations.map(calc => "calculation." + calc.key)
		let calculationColumns = this.insight.calculationColumns.map(calc => calc.key)
		this.clauses.removeWhere(c => !!c.field && c.field.startsWith("calculation.") && !calculations.includes(c.field) && !calculationColumns.includes(c.field))
		this.clauses.forEach(clause => {
			if (clause.orClause && clause.subClauses && clause.subClauses.length > 0) {
				clause.subClauses.removeWhere(s => !!s.field && s.field.startsWith("calculation.") && !calculations.includes(s.field) && !calculationColumns.includes(s.field))
			}
		})

		// Update calculation filter labels if changed
		this.clauses.forEach(clause => {
			if (!clause.orClause && clause.field && clause.field.startsWith("calculation.")) {
				let calculationIndex = this.insight.calculations.findIndex(c => "calculation." + c.key === clause.field)
				if (calculationIndex !== -1) {
					if (clause.label !== this.insight.calculations[calculationIndex].label) {
						clause.label = this.insight.calculations[calculationIndex].label
						if (clause.fieldData) {
							clause.fieldData.label = clause.label
						}
					}
				}
			}
		})

		// Remove or clauses that do not have sub clauses
		this.insight.clauses.removeWhere(c => !!c.orClause && !!c.subClauses && c.subClauses.length === 0)
	}

	async mounted () {
		this.clauses = this.value

		let fieldDataSet = false
		let userFieldDataSet = false

		this.clauses.forEach(clause => {
			if (!clause.id) {
				clause.key = clause.id = Guid.newGuid()
			}
			clause.key = clause.id

			if (!clause.fieldData) {
				let fieldData: CaseColumn | undefined
				if (clause.field?.startsWith("calculation.")) {
					fieldData = this.filterableCalculationColumns.find(c => c.key === clause.field)
				} else {
					fieldData = this.fields.find(f => f.key === clause.field)
				}

				Vue.set(clause, "fieldData", fieldData)
				fieldDataSet = true
			}

			if (!clause.userFieldData) {
				Vue.set(clause, "userFieldData", this.userFieldsWithSubroles(clause).find(f => f.key === clause.userField))
				Vue.set(clause, "userField", clause.userField)

				userFieldDataSet = true
			}
		})

		if (fieldDataSet || userFieldDataSet) {
			this.$emit("input", this.clauses)
		}

		this.$emit("validChanged", this.filtersValid)
	}

	clausesChanged () {
		this.clauses.forEach(clause => {
			if (clause.dynamicParameter) {
				if (!this.dynamicParametersForClause(clause).find(p => p.key === clause.dynamicParameter)) {
					clause.dynamicParameter = null
				}
			}
		})
		this.$emit("input", this.clauses)
	}

	getValidOperators (clause: Clause): OperatorLabel[] {
		if (clause.userFieldData) {
			return this.getValidOperatorsForField(clause.userFieldData)
		}

		return this.getValidOperatorsForField(clause.fieldData!)
	}

	getValidOperatorsForField (field: CaseColumn): OperatorLabel[] {
		const dataType = field.dataType === "calculated" && field.fieldExtensions ? CalculationDataType[field.fieldExtensions.calculationType as CalculationType] : field.dataType

		var operators = field && field.operators ? field.operators : operatorsByFieldType[dataType]
		if (!operators) {
			return []
		}

		if (dataType === "list-data" && !field.dataSourceId) {
			operators = operators.filter((o: Operator) => o !== Operator.Equals)
		}

		return operatorLabels.filter((o: any) => operators.indexOf(o.operator) > -1)
	}

	resetUserField (clause: Clause) {
		Vue.set(clause, "userFieldData", null)
		Vue.set(clause, "userField", null)

		clause.userFieldDataType = undefined
	}

	fieldChanged (clause: Clause) {
		this.resetUserField(clause)

		if (!clause.fieldData || !this.getValidOperatorsForField(clause.fieldData)
			.find(o => o.operator === clause.operator)) {
			clause.operator = null
		}

		clause.value = null
		clause.field = clause.fieldData?.key
		clause.fieldDataType = clause?.fieldData?.fieldDataType ?? DataType.string

		clause.dynamicField = clause && clause.fieldData && clause.fieldData.dynamicField !== undefined ? clause.fieldData.dynamicField : false
		clause.dynamicParameter = null

		this.clausesChanged()

		if (this.getIsFeatureEnabled(TenantFeatures.DataEntryAutoFocus)) {
			setTimeout(() => {
				const nextControl = clause.fieldData?.fieldDataType === DataType.userRole
					? (this.$refs["insightFilterFieldUserRoleSelect" + clause.id] as Vue[])[0].$el.querySelector("input")
					: (this.$refs["insightFilterOperatorSelect" + clause.id] as Vue[])[0].$el.querySelector("input")
				if (nextControl) {
					nextControl.focus()
				}
			}, TRANSITION_FOCUS_DELAY_MS)
		}
	}

	userFieldChanged (clause: Clause) {
		Vue.set(clause, "operator", null)
		clause.value = null
		Vue.set(clause, "userField", clause.userFieldData ? clause.userFieldData.key : null)
		clause.userFieldDataType = clause && clause.userFieldData ? clause.userFieldData.fieldDataType : 0
		clause.dynamicField = clause && clause.userFieldData ? !!clause.userFieldData.dynamicField : false
		clause.dynamicParameter = null

		this.clausesChanged()

		if (this.getIsFeatureEnabled(TenantFeatures.DataEntryAutoFocus)) {
			setTimeout(() => {
				const operatorControl =
					(this.$refs["insightFilterOperatorSelect" + clause.id] as Vue[])[0].$el.querySelector("input")
				if (operatorControl) {
					operatorControl.focus()
				}
			}, TRANSITION_FOCUS_DELAY_MS)
		}
	}

	dynamicValueChanged (clause: any, newValue: boolean) {
		Vue.set(clause, "dynamicValue", newValue === null ? false : newValue)
		Vue.set(clause, "value", null)

		this.clausesChanged()
	}

	searchClauseParametersChanged (clause: Clause, searchClauseParameters: SearchClauseParameters) {
		Vue.set(clause, "searchClauseParameters", searchClauseParameters)

		this.clausesChanged()
	}

	addFilter () {
		const key = Guid.newGuid()

		this.clauses.push({
			key,
			id: key,
			dynamicField: false,
			dynamicValue: false,
			fieldDataType: 0,
			fieldData: undefined,
			label: "",
			value: null,
			searchClauseParameters: getDefaultSearchClauseParameters(),
			dynamicParameter: null,
			parentClauseId: this.parentClauseId
		})

		if (this.getIsFeatureEnabled(TenantFeatures.DataEntryAutoFocus)) {
			setTimeout(() => {
				const newFilterControl =
					(this.$refs["insightFilterFieldSelect" + key] as Vue[])[0].$el.querySelector("input")
				if (newFilterControl) newFilterControl.focus()
			}, TRANSITION_FOCUS_DELAY_MS)
		}
	}

	addOrFilter () {
		const key = Guid.newGuid()
		this.clauses.push({
			key,
			id: key,
			dynamicField: false,
			dynamicValue: false,
			fieldDataType: 0,
			label: "",
			value: null,
			orClause: true,
			subClauses: [],
			isValid: true,
			searchClauseParameters: getDefaultSearchClauseParameters()
		})

		setTimeout(() => {
			const newFilterControl =
				(this.$refs["insightFilterOrFilter" + key] as Vue[])[0].$el.querySelector("div")?.querySelector("button")
			if (newFilterControl) newFilterControl.focus()
		}, TRANSITION_FOCUS_DELAY_MS)
	}

	removeFilter (filter: Clause) {
		this.clauses.splice(this.clauses.indexOf(filter), 1)
		this.clausesChanged()
	}

	isFilterValid (filter: Clause): boolean {
		if (filter.orClause) {
			return !!filter.isValid && !!filter.subClauses && (filter.subClauses.length > 0)
		}

		let currentFilterIndex = this.clauses.findIndex(c => c.id === filter.id)
		let isFilterClauseComplete = (this.caseFieldFilterValid(filter) || this.userFieldFilterValid(filter)) && this.searchClauseParametersValid(filter)
		if (this.isCalculationFilter(filter.fieldData?.key)) {
			if (this.insight.groupBy === null || this.insight.groupBy === undefined || this.insight.groupBy === "") {
				// Calculation filters are not valid if groupBy is not set
				if (currentFilterIndex !== -1) {
					this.clauses[currentFilterIndex].invalidErrorInfo = "Calculation filters can only be applied on grouped insights."
				}
				return false
			}
		} else {
			// If filter is a normal filter applied within an or block and calculation filters have already been applied
			if (!!filter.parentClauseId && this.calculationFiltersExist) {
				if (currentFilterIndex !== -1) {
					this.clauses[currentFilterIndex].invalidErrorInfo = null
				}
				return false
			}
		}

		if (currentFilterIndex !== -1) {
			this.clauses[currentFilterIndex].invalidErrorInfo = null
		}
		return isFilterClauseComplete
	}

	private caseFieldFilterValid (filter: Clause) : boolean {
		return !!filter.fieldData &&
			filter.operator !== undefined &&
			filter.operator !== null &&
			(filter.operator === Operator.IsSet || filter.operator === Operator.NotSet || (!!filter.dynamicParameter || (filter.value !== undefined && filter.value !== null)))
	}

	private userFieldFilterValid (filter: Clause) : boolean {
		return filter &&
			!!filter.userFieldData &&
			filter.operator !== undefined &&
			filter.operator !== null &&
			(filter.operator === Operator.IsSet || filter.operator === Operator.NotSet || (!!filter.dynamicParameter || (filter.value !== undefined && filter.value !== null)))
	}

	private searchClauseParametersValid (filter: Clause) : boolean {
		if (!filter) {
			return false
		}

		switch (filter.value) {
			case "DateRange":
				return !!filter.searchClauseParameters && !!filter.searchClauseParameters.startDate && !!filter.searchClauseParameters.endDate
			case "MonthRange":
				return !!filter.searchClauseParameters && !!filter.searchClauseParameters.startMonthRange && !!filter.searchClauseParameters.endMonthRange
			case "WeekNumber":
				return !!filter.searchClauseParameters && !!filter.searchClauseParameters.weekNumber
		}

		return true
	}

	private userFieldsWithSubroles (clause: Clause): CaseColumn[] {
		return [
			...this.userFields,
			...(clause.fieldData && clause.fieldData.fieldExtensions && clause.fieldData.fieldExtensions.subroles
				? clause.fieldData.fieldExtensions.subroles.map((subRole: UserRole) => {
					return {
						label: "Subrole: " + subRole.title,
						key: "subrole." + subRole.title,
						dataType: "subrole",
						fieldDataType: DataType.subrole
					}
				})
				: []
			)
		]
	}

	setValid (filter: any, valid: boolean) {
		Vue.set(filter, "isValid", valid)
		this.clausesChanged()
	}

	dynamicParametersForClause (clause: Clause): DynamicParameter[] {
		if (!this.dynamicParameters) return []

		if (clause.operator === null ||
			clause.operator === undefined ||
			clause.operator === Operator.IsSet ||
			clause.operator === Operator.NotSet) {
			return []
		}

		return this.dynamicParameters!.filter(dynamicParameter =>
			this.dynamicParameterValidForClause(clause, dynamicParameter)
		)
	}

	private dynamicParameterValidForClause (clause: Clause, dynamicParameter: DynamicParameter) : boolean {
		let fieldData = clause.fieldData
		let fieldDataType = clause.fieldDataType
		if (this.isUserRoleWithUserFieldSet(clause)) {
			fieldData = clause.userFieldData ?? undefined
			fieldDataType = clause.userFieldDataType!
		}

		if (dynamicParameter.dataType === DataType.array && !!dynamicParameter.dataSourceId) {
			return fieldData?.dataSourceId === dynamicParameter.dataSourceId
		} else if (fieldDataType === DataType.dateTime) {
			if (clause.operator === Operator.DateRange) {
				return dynamicParameter.dataType === DataType.dateRange
			} else {
				return dynamicParameter.dataType === DataType.dateTime
			}
		} else if (this.parameterTypes[dynamicParameter.dataType]) {
			return this.parameterTypes[dynamicParameter.dataType].fieldDataTypes.includes(this.getDynamicParameterClauseDataType(clause, fieldDataType))
		} else {
			return false
		}
	}

	private getDynamicParameterClauseDataType (clause: Clause, fieldDataType: DataType) : DataType {
		if (fieldDataType !== DataType.userRole) {
			return fieldDataType
		}

		if (clause.operator === Operator.Equals ||
			clause.operator === Operator.GreaterThan ||
			clause.operator === Operator.LessThan) {
			return DataType.number
		} else {
			if (clause.operator === Operator.InTeam) {
				return DataType.team
			}
		}
		return fieldDataType
	}

	private isUserRoleField (clause: Clause) : boolean {
		return clause.fieldDataType === DataType.userRole ||
			(!clause.dynamicField && clause.fieldData?.key === "assigneeId") || // Assignee
			(!clause.dynamicField && clause.fieldData?.key === "users") // Collaborators
	}

	private isUserRoleWithUserFieldSet (clause: Clause) : boolean {
		return this.isUserRoleField(clause) && !!clause.userFieldData
	}

	private getColumnForParameterValue (clause: Clause): CaseColumn {
		if (clause.userField != null) {
			return clause.userFieldData!
		}
		return clause.fieldData!
	}

	/** Hides clasues that are adminOnly if the user isn't admin */
	private getNonAdminClauses (clause: Clause) {
		if (!this.me.isAdmin) {
			var caseCol = caseColumns.find(col => col.key === clause.field)
			if (caseCol !== undefined && caseCol.adminOnly) {
				return false	// Clause is admin only and user isn't admin.
			} else return true	// Clause isn't admin only.
		} else return true		// User is admin.
	}

	private async operatorChanged (clause: Clause, newValue: Operator) {
		const oldValue = clause.operator
		const oldValueValid = oldValue !== null && oldValue !== undefined

		clause.operator = newValue
		Vue.set(this.clauses, this.clauses.indexOf(clause), clause)

		this.clausesChanged()

		if (clause.operator !== undefined && clause.operator !== null) {
			// clear existing value for the clause if appropriate for new operator
			if (oldValue !== newValue && clause.value && clause.fieldData) {
				let avoidValueCarryOverOperators: Operator[] = []

				switch (clause.fieldDataType) {
					case DataType.userRole:
						avoidValueCarryOverOperators = [Operator.Contains, Operator.InTeam, Operator.UserTimeLocation]
						break
				}

				// clear the value if we're changing to / from an operator that values shouldn't be carried over for
				if (avoidValueCarryOverOperators.includes(clause.operator) || (oldValueValid && avoidValueCarryOverOperators.includes(oldValue!))) {
					Vue.set(clause, "value", null)
					this.clausesChanged()
				}
			}

			// handle auto-focus if enabled
			await this.handleOperatorChangedAutoFocus(clause)
		}
	}

	private async handleOperatorChangedAutoFocus (clause: Clause): Promise<void> {
		if (this.getIsFeatureEnabled(TenantFeatures.DataEntryAutoFocus)) {
			if (clause.fieldData?.dataSourceId) {
				await this.$nextTick()
				const valueControl =
					(this.$refs["insightFilterValue" + clause.id] as Vue[])[0].$el
				if (valueControl) {
					// use event for 'value' field as it can take longer to render e.g. when loading a data source
					this.valueFieldEventHandlerControl = valueControl.parentElement!
					this.valueFieldEventHandlerControl.addEventListener("animationend", this.operatorAnimationEnded)
					// ensure the event always occurs by hiding and showing the value field momentarily
					Vue.set(this, "hideValueField", true)
					await this.$nextTick()
					Vue.set(this, "hideValueField", false)
				}
			} else if (clause.fieldData?.dataType === "date-time") {
				// date-time value picker takes longer to render in some situations
				await this.$nextTick()
				await this.$nextTick()
				setTimeout(() => {
					const valueControl =
						(this.$refs["insightFilterValue" + clause.id] as Vue[])[0].$el.querySelector("input")
					if (valueControl) valueControl.focus()
				}, TRANSITION_FOCUS_DELAY_MS + 50)
			} else {
				setTimeout(() => {
					const valueControl =
						(this.$refs["insightFilterValue" + clause.id] as Vue[])[0].$el.querySelector("input")
					if (valueControl) valueControl.focus()
				}, TRANSITION_FOCUS_DELAY_MS)
			}
		}
	}

	operatorAnimationEnded (event: any) {
		this.valueFieldEventHandlerControl!.removeEventListener("animationend", this.operatorAnimationEnded)
		const inputControl = event.srcElement.querySelector("input")
		if (inputControl) inputControl.focus()
	}

	valueTableDataVisible (clause: Clause): boolean {
		return clause && !!clause.fieldData && clause.operator !== null && clause.operator !== undefined &&
			!(clause.operator === Operator.IsSet || clause.operator === Operator.NotSet)
	}
}

